import React, { useEffect, useState, useCallback } from "react";
// ────────────────────────────────────────────────────────────
// Orchestrator Dashboard
// Live oversigt over autonome multi-agent sessions.
// Polling hvert 5. sek. — render alt med backdrop-blur og motion.
// ────────────────────────────────────────────────────────────
const api = async (sti, metode = 'GET', data = null) => {
 const res = await fetch('/api' + sti, {
 method: metode,
 credentials: 'include',
 headers: { 'Content-Type': 'application/json' },
 body: data ? JSON.stringify(data) : null,
 });
 if (!res.ok) {
 const err = await res.json().catch(() => ({}));
 throw new Error(err.fejl || `HTTP ${res.status}`);
 }
 return res.json();
};
const FARVE = {
 bg: 'rgba(255,255,255,0.85)',
 blur: 'blur(20px)',
 primary: '#2322be',
 accent: '#3fd4c7',
 gold: '#c9a84c',
 text: '#1a1a2e',
 textMuted: '#4a4a6a',
 shadow: '0 8px 32px rgba(35,34,190,0.12)',
 radius: 16,
};
const STATUS_FARVER = {
 IGANG: '#3fd4c7',
 AFVENTER_GODKENDELSE: '#c9a84c',
 IMPLEMENTERING: '#2322be',
 GODKENDT: '#27ae60',
 AFVIST: '#e74c3c',
 FULDFOERT: '#27ae60',
 FEJLET: '#e74c3c',
 ANNULLERET: '#7f8c8d',
};
const FASE_LABELS = {
 FASE_1: '1. Indsamling',
 FASE_2: '2. Debat',
 SYNTESE: '3. Syntese',
 GODKENDELSE: '4. Godkendelse',
 IMPLEMENTERING: '5. Implementering',
 AFSLUTNING: '6. Afslutning',
};
function StatusBadge({ status }) {
 const farve = STATUS_FARVER[status] || '#7f8c8d';
 return (
 <span style={{
 background: farve + '22',
 color: farve,
 padding: '4px 10px',
 borderRadius: 8,
 fontSize: 11,
 fontWeight: 700,
 letterSpacing: 0.3,
 }}>{status}</span>
 );
}
function FaseStepper({ fase }) {
 const faser = ['FASE_1', 'FASE_2', 'SYNTESE', 'GODKENDELSE', 'IMPLEMENTERING', 'AFSLUTNING'];
 const aktivIdx = faser.indexOf(fase);
 return (
 <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
 {faser.map((f, i) => (
 <React.Fragment key={f}> <div style={{
 padding: '4px 8px',
 borderRadius: 6,
 background: i <= aktivIdx ? FARVE.primary : '#e0e0e0',
 color: i <= aktivIdx ? '#fff' : '#999',
 fontSize: 10,
 fontWeight: 600,
 opacity: i === aktivIdx ? 1 : (i < aktivIdx ? 0.6 : 0.4),
 transition: 'all 0.3s',
 }}>{FASE_LABELS[f]}</div>
 {i < faser.length - 1 && <span style={{ color: i < aktivIdx ? FARVE.primary : '#ccc' }}>→</span>}
 </React.Fragment>
 ))}
 </div>
 );
}
function SessionRow({ s, onClick }) {
 const dato = new Date(s.opdateret_kl);
 return (
 <div onClick={onClick} style={{
 background: FARVE.bg,
 backdropFilter: FARVE.blur,
 padding: 16,
 borderRadius: FARVE.radius,
 marginBottom: 12,
 cursor: 'pointer',
 boxShadow: FARVE.shadow,
 transition: 'transform 0.3s ease, box-shadow 0.3s ease',
 border: '1px solid rgba(35,34,190,0.08)',
 }}
 onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 20px 60px rgba(35,34,190,0.20)'; }}
 onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = FARVE.shadow; }}
 > <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}> <div style={{ flex: 1, paddingRight: 12 }}> <h3 style={{ margin: 0, color: FARVE.text, fontSize: 16, fontWeight: 600 }}>{s.titel}</h3> <p style={{ margin: '4px 0 0 0', color: FARVE.textMuted, fontSize: 13 }}>{(s.beskrivelse || '').slice(0, 140)}{s.beskrivelse?.length > 140 ? '…' : ''}</p> </div> <StatusBadge status={s.status} /> </div> <div style={{ marginTop: 10 }}> <FaseStepper fase={s.fase} /> </div> <div style={{ display: 'flex', gap: 16, marginTop: 10, fontSize: 11, color: FARVE.textMuted }}> <span> {(s.relevante_agenter || []).length} agenter</span>
 {s.debat_runde > 0 && <span> Debat-runde {s.debat_runde}/{s.max_debat_runder}</span>}
 {s.konsensus_score !== null && s.konsensus_score !== undefined && (
 <span style={{ color: s.konsensus_score >= 0.75 ? '#27ae60' : FARVE.gold, fontWeight: 600 }}>
 Konsensus: {(s.konsensus_score * 100).toFixed(0)}%
 </span>
 )}
 <span style={{ marginLeft: 'auto' }}> {dato.toLocaleString('da-DK')}</span> </div> </div>
 );
}
// Udtræk hvad en agent leverede fra dens bidrag-JSON
function udtraekLeverancer(bidrag) {
 if (!bidrag || typeof bidrag !== 'object') return [];
 const linjer = [];
 if (Array.isArray(bidrag.implementeret)) {
 bidrag.implementeret.forEach(s => linjer.push({ tekst: s, type: 'implementeret' }));
 }
 if (bidrag.pr_url) linjer.push({ tekst: `PR: ${bidrag.pr_url}`, type: 'pr', url: bidrag.pr_url, status: bidrag.pr_status });
 if (Array.isArray(bidrag.fund) && !bidrag.implementeret) {
 bidrag.fund.slice(0, 3).forEach(s => linjer.push({ tekst: String(s), type: 'fund' }));
 }
 if (Array.isArray(bidrag.anbefalinger) && !bidrag.implementeret) {
 bidrag.anbefalinger.slice(0, 2).forEach(s => linjer.push({ tekst: String(s), type: 'anbefaling' }));
 }
 if (linjer.length === 0 && bidrag.anbefaling) linjer.push({ tekst: bidrag.anbefaling, type: 'anbefaling' });
 if (linjer.length === 0 && bidrag.resume) linjer.push({ tekst: bidrag.resume.slice(0, 120), type: 'resume' });
 return linjer;
}
function HvemLavedeHvadTabel({ bidrag }) {
 const impl = bidrag.filter(b => b.fuldfoert_kl && !b.fejlet);
 if (impl.length === 0) return null;
 const ikonForType = { implementeret: '', pr: '', fund: '', anbefaling: '', resume: '' };
 return (
 <div style={{ background: FARVE.bg, backdropFilter: FARVE.blur, padding: 20, borderRadius: FARVE.radius, marginBottom: 20, boxShadow: FARVE.shadow }}> <h2 style={{ margin: '0 0 14px 0', color: FARVE.text, fontSize: 16 }}> Hvem lavede hvad</h2> <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}> <thead> <tr style={{ borderBottom: '2px solid rgba(35,34,190,0.1)' }}> <th style={{ textAlign: 'left', padding: '4px 8px', color: FARVE.textMuted, fontWeight: 600, width: 110 }}>Agent</th> <th style={{ textAlign: 'left', padding: '4px 8px', color: FARVE.textMuted, fontWeight: 600 }}>Leverancer</th> <th style={{ textAlign: 'left', padding: '4px 8px', color: FARVE.textMuted, fontWeight: 600, width: 80 }}>Fase</th> </tr> </thead> <tbody>
 {impl.map(b => {
 const leverancer = udtraekLeverancer(b.bidrag);
 return (
 <tr key={b.id} style={{ borderBottom: '1px solid rgba(0,0,0,0.06)', verticalAlign: 'top' }}> <td style={{ padding: '8px', fontWeight: 700, color: FARVE.primary }}>{b.agent_navn}</td> <td style={{ padding: '8px' }}>
 {leverancer.length === 0
 ? <span style={{ color: FARVE.textMuted, fontStyle: 'italic' }}>—</span>
 : leverancer.map((l, i) => (
 <div key={i} style={{ marginBottom: i < leverancer.length - 1 ? 4 : 0 }}> <span style={{ marginRight: 5 }}>{ikonForType[l.type] || '·'}</span>
 {l.url
 ? <a href={l.url} target="_blank" rel="noreferrer" style={{ color: FARVE.primary }}>{l.tekst}</a>
 : <span style={{ color: l.type === 'pr' ? FARVE.accent : FARVE.text }}>{l.tekst}</span>
 }
 {l.status && <span style={{ marginLeft: 6, fontSize: 10, color: l.status === 'MERGET' ? '#27ae60' : FARVE.gold, fontWeight: 700 }}>{l.status}</span>}
 </div>
 ))
 }
 </td> <td style={{ padding: '8px', fontSize: 10, color: FARVE.textMuted }}>{b.fase}{b.runde > 1 ? ` r${b.runde}` : ''}</td> </tr>
 );
 })}
 </tbody> </table> </div>
 );
}
function KommentarBoks({ id, onGemt }) {
 const [tekst, setTekst] = useState('');
 const [sender, setSender] = useState(false);
 const [besked, setBesked] = useState(null);
 const gem = async () => {
 if (!tekst.trim()) return;
 setSender(true);
 setBesked(null);
 try {
 await api(`/os/orchestrator/sessions/${id}/kommentar`, 'POST', { tekst });
 setTekst('');
 setBesked('Kommentar gemt');
 setTimeout(() => setBesked(null), 3000);
 onGemt?.();
 } catch (e) {
 setBesked('Fejl: ' + e.message);
 } finally {
 setSender(false);
 }
 };
 return (
 <div style={{ background: FARVE.bg, backdropFilter: FARVE.blur, padding: 16, borderRadius: FARVE.radius, marginBottom: 16, boxShadow: FARVE.shadow, border: `1px solid ${FARVE.gold}44` }}> <div style={{ fontSize: 13, fontWeight: 600, color: FARVE.gold, marginBottom: 8 }}> Tilføj kommentar / korrektion</div> <textarea
 value={tekst}
 onChange={e => setTekst(e.target.value)}
 placeholder="Skriv en kommentar til agenternes arbejde — fx 'Ret fejl X' eller 'Fokusér mere på Y'. Vises i log og sendes med til næste fase."
 style={{ width: '100%', minHeight: 70, padding: 10, borderRadius: 8, border: `1px solid ${FARVE.gold}66`, fontFamily: 'inherit', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }}
 onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) gem(); }}
 /> <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 8 }}> <button onClick={gem} disabled={sender || !tekst.trim()} style={{
 background: FARVE.gold, color: '#fff', border: 'none', padding: '8px 16px',
 borderRadius: 8, cursor: sender || !tekst.trim() ? 'default' : 'pointer',
 fontWeight: 600, fontSize: 13, opacity: !tekst.trim() ? 0.5 : 1,
 }}>{sender ? 'Gemmer…' : 'Gem kommentar'}</button> <span style={{ fontSize: 11, color: FARVE.textMuted }}>Ctrl+Enter</span>
 {besked && <span style={{ fontSize: 12, color: besked.startsWith('Fejl') ? '#e74c3c' : '#27ae60', fontWeight: 600 }}>{besked}</span>}
 </div> </div>
 );
}
function SessionDetail({ id, onTilbage, onAction }) {
 const [data, setData] = useState(null);
 const [fejl, setFejl] = useState(null);
 const [kommentar, setKommentar] = useState('');
 const [sender, setSender] = useState(false);
 const hent = useCallback(async () => {
 try {
 const d = await api(`/os/orchestrator/sessions/${id}`);
 setData(d);
 } catch (e) {
 setFejl(e.message);
 }
 }, [id]);
 useEffect(() => {
 hent();
 const t = setInterval(hent, 5000);
 return () => clearInterval(t);
 }, [hent]);
 if (fejl) return <div style={{ padding: 20, color: '#e74c3c' }}>Fejl: {fejl}</div>;
 if (!data) return <div style={{ padding: 20 }}>Henter…</div>;
 const { session, bidrag, tasks = [], log } = data;
 const bidragPerFase = bidrag.reduce((acc, b) => {
 const k = `${b.fase}_r${b.runde}`;
 if (!acc[k]) acc[k] = { fase: b.fase, runde: b.runde, items: [] };
 acc[k].items.push(b);
 return acc;
 }, {});
 const klarTilGodkendelse = session.status === 'AFVENTER_GODKENDELSE';
 const handleGodkend = async (handling) => {
 setSender(true);
 try {
 await api(`/os/orchestrator/sessions/${id}/${handling}`, 'POST', {
 kommentar,
 ...(handling === 'godkend' ? { implementerings_agenter: session.relevante_agenter } : {}),
 });
 onAction?.();
 hent();
 } catch (e) {
 setFejl(e.message);
 } finally {
 setSender(false);
 }
 };
 return (
 <div style={{ padding: 20 }}> <button onClick={onTilbage} style={{
 background: 'transparent', border: 'none', color: FARVE.primary, fontSize: 14, cursor: 'pointer', marginBottom: 16,
 }}>← Tilbage til oversigt</button> <div style={{ background: FARVE.bg, backdropFilter: FARVE.blur, padding: 24, borderRadius: FARVE.radius, marginBottom: 20, boxShadow: FARVE.shadow }}> <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}> <h1 style={{ margin: 0, color: FARVE.text, fontSize: 22, fontWeight: 700 }}>{session.titel}</h1> <StatusBadge status={session.status} /> </div> <p style={{ color: FARVE.textMuted, lineHeight: 1.6 }}>{session.beskrivelse}</p> <div style={{ marginTop: 16 }}><FaseStepper fase={session.fase} /></div> <div style={{ marginTop: 16, fontSize: 12, color: FARVE.textMuted }}> <strong>Agenter:</strong> {(session.relevante_agenter || []).join(', ') || 'Ikke valgt endnu'}
 </div>
 {session.konsensus_score !== null && session.konsensus_score !== undefined && (
 <div style={{ marginTop: 8, fontSize: 14 }}> <strong>Konsensus-score:</strong>{' '}
 <span style={{ color: session.konsensus_score >= 0.75 ? '#27ae60' : FARVE.gold, fontWeight: 700 }}>
 {(session.konsensus_score * 100).toFixed(0)}%
 </span> </div>
 )}
 </div>
 {klarTilGodkendelse && session.syntese_anbefaling && (
 <div style={{
 background: 'linear-gradient(135deg, #c9a84c11, #2322be11)',
 backdropFilter: FARVE.blur,
 padding: 24,
 borderRadius: FARVE.radius,
 marginBottom: 20,
 border: `2px solid ${FARVE.gold}`,
 boxShadow: FARVE.shadow,
 }}> <h2 style={{ margin: '0 0 16px 0', color: FARVE.text, fontSize: 18 }}> Syntese — afventer din godkendelse</h2>
 {session.syntese_anbefaling?.fase_1_resultater ? (
 <div style={{ marginBottom: 12 }}>
 {(session.syntese_anbefaling.fase_1_resultater || []).map((r, i) => (
 <div key={i} style={{ marginBottom: 10, padding: 12, background: 'rgba(0,0,0,0.04)', borderRadius: 8 }}> <div style={{ fontWeight: 700, color: FARVE.primary, marginBottom: 6, fontSize: 13 }}>{r.afdeling}</div> <pre style={{ fontSize: 11, color: FARVE.text, margin: 0, whiteSpace: 'pre-wrap', maxHeight: 200, overflow: 'auto' }}>
 {typeof r.resultat === 'string' ? r.resultat : JSON.stringify(r.resultat, null, 2)}
 </pre> </div>
 ))}
 {(session.syntese_anbefaling.fejlede || []).length > 0 && (
 <div style={{ fontSize: 12, color: '#e74c3c' }}>Fejlede agenter: {session.syntese_anbefaling.fejlede.join(', ')}</div>
 )}
 </div>
 ) : (
 <pre style={{ background: 'rgba(0,0,0,0.04)', padding: 16, borderRadius: 8, fontSize: 12, overflowX: 'auto', maxHeight: 400, color: FARVE.text }}>
 {JSON.stringify(session.syntese_anbefaling, null, 2)}
 </pre>
 )}
 <textarea
 value={kommentar}
 onChange={e => setKommentar(e.target.value)}
 placeholder="Eventuel kommentar..."
 style={{ width: '100%', minHeight: 60, padding: 10, borderRadius: 8, border: '1px solid #ddd', marginTop: 12, fontFamily: 'inherit' }}
 /> <div style={{ display: 'flex', gap: 12, marginTop: 12 }}> <button onClick={() => handleGodkend('godkend')} disabled={sender} style={{
 background: '#27ae60', color: '#fff', border: 'none', padding: '12px 24px',
 borderRadius: 8, fontWeight: 700, cursor: sender ? 'wait' : 'pointer', fontSize: 14,
 }}> GODKENDT</button> <button onClick={() => handleGodkend('afvis')} disabled={sender} style={{
 background: '#e74c3c', color: '#fff', border: 'none', padding: '12px 24px',
 borderRadius: 8, fontWeight: 700, cursor: sender ? 'wait' : 'pointer', fontSize: 14,
 }}> AFVIST</button> </div> </div>
 )}
 {tasks.length > 0 && (
 <div style={{ background: FARVE.bg, backdropFilter: FARVE.blur, padding: 20, borderRadius: FARVE.radius, marginBottom: 20, boxShadow: FARVE.shadow }}> <h2 style={{ margin: '0 0 14px 0', color: FARVE.text, fontSize: 16 }}> Agent-opgaver ({tasks.length})</h2> <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
 {tasks.map(t => {
 const statusFarve = { FULDFOERT: '#27ae60', FEJLET: '#e74c3c', KOERER: FARVE.accent, AFVENTER: '#7f8c8d', ANNULLERET: '#aaa' }[t.status] || FARVE.textMuted;
 return (
 <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8, background: 'rgba(0,0,0,0.03)', border: `1px solid ${statusFarve}33` }}> <span style={{ fontSize: 14 }}>{t.status === 'FULDFOERT' ? '' : t.status === 'FEJLET' ? '' : t.status === 'KOERER' ? '' : ''}</span> <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: FARVE.text }}>{t.afdeling}</span> <span style={{ fontSize: 11, color: statusFarve, fontWeight: 700 }}>{t.status}</span>
 {t.afsluttet_kl && <span style={{ fontSize: 10, color: FARVE.textMuted }}>{new Date(t.afsluttet_kl).toLocaleTimeString('da-DK')}</span>}
 </div>
 );
 })}
 </div>
 {tasks.some(t => t.status === 'AFVENTER') && (
 <p style={{ fontSize: 12, color: FARVE.textMuted, marginTop: 10 }}>
 os-dispatcher behandler opgaverne automatisk (kører hvert 5. minut)
 </p>
 )}
 </div>
 )}
 <HvemLavedeHvadTabel bidrag={bidrag} /> <KommentarBoks id={id} onGemt={hent} /> <h2 style={{ color: '#fff', fontSize: 18, marginTop: 24, marginBottom: 12 }}>Agent-bidrag</h2>
 {Object.entries(bidragPerFase).map(([k, gruppe]) => (
 <div key={k} style={{ marginBottom: 16 }}> <h3 style={{ color: '#fff', fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
 {FASE_LABELS[gruppe.fase] || gruppe.fase}{gruppe.runde > 1 ? ` (runde ${gruppe.runde})` : ''}
 </h3>
 {gruppe.items.map(b => (
 <div key={b.id} style={{
 background: FARVE.bg, backdropFilter: FARVE.blur, padding: 14, borderRadius: 12, marginBottom: 8,
 border: b.fejlet ? '1px solid #e74c3c' : '1px solid rgba(35,34,190,0.08)',
 }}> <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}> <strong style={{ color: FARVE.primary }}>{b.agent_navn}</strong> <span style={{ fontSize: 11, color: FARVE.textMuted }}>
 {b.fuldfoert_kl ? ' ' + new Date(b.fuldfoert_kl).toLocaleString('da-DK') : (b.fejlet ? ' Fejlet' : ' Inviteret')}
 </span> </div>
 {b.konvergens_signal && (
 <div style={{ fontSize: 11, color: FARVE.accent, marginBottom: 4 }}>Signal: {b.konvergens_signal}</div>
 )}
 {b.bidrag && Object.keys(b.bidrag).length > 0 && (
 <pre style={{ fontSize: 11, color: FARVE.text, background: 'rgba(0,0,0,0.03)', padding: 8, borderRadius: 6, margin: 0, whiteSpace: 'pre-wrap', maxHeight: 200, overflow: 'auto' }}>
 {JSON.stringify(b.bidrag, null, 2)}
 </pre>
 )}
 </div>
 ))}
 </div>
 ))}
 <h2 style={{ color: '#fff', fontSize: 18, marginTop: 24, marginBottom: 12 }}>Log</h2> <div style={{ background: FARVE.bg, backdropFilter: FARVE.blur, borderRadius: FARVE.radius, padding: 14, maxHeight: 300, overflow: 'auto' }}>
 {log.length === 0 ? <em style={{ color: FARVE.textMuted }}>Ingen log endnu</em> : log.map(l => {
 const erRene = l.niveau === 'RENE_KOMMENTAR';
 return (
 <div key={l.id} style={{
 fontSize: 11, marginBottom: erRene ? 8 : 4, fontFamily: 'monospace',
 color: l.niveau === 'FEJL' ? '#e74c3c' : (erRene ? FARVE.gold : (l.niveau === 'ADVARSEL' ? '#e67e22' : FARVE.text)),
 background: erRene ? FARVE.gold + '11' : 'transparent',
 padding: erRene ? '6px 8px' : '0',
 borderRadius: erRene ? 6 : 0,
 border: erRene ? `1px solid ${FARVE.gold}44` : 'none',
 }}> <span style={{ color: FARVE.textMuted }}>{new Date(l.oprettet_kl).toLocaleTimeString('da-DK')}</span>
 {' '}[<strong>{l.niveau}</strong>{l.fase ? '/' + l.fase : ''}] {l.besked}
 </div>
 );
 })}
 </div> </div>
 );
}
const AGENTER_LISTE = [
 { id: 'kode', label: ' Kode', beskrivelse: 'Implementering, kodeanalyse, PR' },
 { id: 'design_ux', label: ' Design/UX', beskrivelse: 'UX-analyse, wireframes, accessibility' },
 { id: 'analyse', label: ' Analyse', beskrivelse: 'Data, KPIer, mønstre' },
 { id: 'qa_testing', label: ' QA/Test', beskrivelse: 'Testplaner, acceptkriterier' },
 { id: 'marketing', label: ' Marketing', beskrivelse: 'SEO, content, kommunikation' },
 { id: 'oekonomi', label: ' Økonomi', beskrivelse: 'ROI, cashflow, budget' },
 { id: 'overvaagning', label: ' Overvågning', beskrivelse: 'Drift, sikkerhed, health' },
 { id: 'ai-specialist', label: ' AI', beskrivelse: 'Prompt-design, Claude API' },
 { id: 'investering-vaekst', label: ' Vækst', beskrivelse: 'B2B/B2G, skalering' },
 { id: 'strategisk-raadgiver', label: ' Strategi', beskrivelse: 'Juridisk, risici' },
 { id: 'kommunikation', label: ' Komm.', beskrivelse: 'Notifikationer, info' },
 { id: 'koordinator', label: ' Koordinator', beskrivelse: 'Workflow, orkestrering' },
];
function NySessionForm({ onCreated, onCancel }) {
 const [titel, setTitel] = useState('');
 const [beskrivelse, setBeskrivelse] = useState('');
 const [valgteAgenter, setValgteAgenter] = useState(['kode', 'design_ux', 'analyse', 'qa_testing']);
 const [sender, setSender] = useState(false);
 const [fejl, setFejl] = useState(null);
 const toggleAgent = (id) => {
 setValgteAgenter(prev =>
 prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]
 );
 };
 const submit = async () => {
 if (valgteAgenter.length === 0) {
 setFejl('Vælg mindst én agent');
 return;
 }
 setSender(true);
 setFejl(null);
 try {
 // Trin 1: Opret session
 const result = await api('/os/orchestrator/sessions', 'POST', {
 titel, beskrivelse, kilde_type: 'rene_prompt', relevante_agenter: valgteAgenter,
 });
 // Trin 2: Start session (opretter opgaver for hver agent)
 await api(`/os/orchestrator/sessions/${result.session.id}/start`, 'POST', {});
 onCreated?.();
 } catch (e) {
 setFejl(e.message);
 } finally {
 setSender(false);
 }
 };
 return (
 <div style={{ background: FARVE.bg, backdropFilter: FARVE.blur, padding: 24, borderRadius: FARVE.radius, marginBottom: 20, boxShadow: FARVE.shadow }}> <h3 style={{ margin: '0 0 16px 0', color: FARVE.text }}>Ny orchestrator-session</h3> <input type="text" value={titel} onChange={e => setTitel(e.target.value)} placeholder="Titel (kort)"
 style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd', marginBottom: 10, fontSize: 14 }} /> <textarea value={beskrivelse} onChange={e => setBeskrivelse(e.target.value)} placeholder="Idé eller opgavebeskrivelse — beskriv hvad agentteamet skal arbejde med..."
 style={{ width: '100%', minHeight: 100, padding: 10, borderRadius: 8, border: '1px solid #ddd', marginBottom: 12, fontFamily: 'inherit', fontSize: 14 }} /> <div style={{ marginBottom: 12 }}> <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}> <span style={{ fontSize: 13, fontWeight: 600, color: FARVE.text }}>
 Agenter ({valgteAgenter.length} valgt)
 </span> <div style={{ display: 'flex', gap: 8 }}> <button onClick={() => setValgteAgenter(AGENTER_LISTE.map(a => a.id))}
 style={{ fontSize: 11, padding: '3px 8px', borderRadius: 4, border: `1px solid ${FARVE.primary}`, background: 'transparent', color: FARVE.primary, cursor: 'pointer' }}>
 Alle
 </button> <button onClick={() => setValgteAgenter([])}
 style={{ fontSize: 11, padding: '3px 8px', borderRadius: 4, border: '1px solid #ddd', background: 'transparent', color: FARVE.textMuted, cursor: 'pointer' }}>
 Ryd
 </button> </div> </div> <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 6 }}>
 {AGENTER_LISTE.map(a => {
 const valgt = valgteAgenter.includes(a.id);
 return (
 <label key={a.id} style={{
 display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 10px',
 borderRadius: 8, cursor: 'pointer',
 border: `1px solid ${valgt ? FARVE.primary : '#e0e0e0'}`,
 background: valgt ? FARVE.primary + '11' : 'rgba(255,255,255,0.5)',
 transition: 'all 0.2s',
 }}> <input type="checkbox" checked={valgt} onChange={() => toggleAgent(a.id)}
 style={{ marginTop: 2, accentColor: FARVE.primary }} /> <div> <div style={{ fontSize: 12, fontWeight: 600, color: valgt ? FARVE.primary : FARVE.text }}>{a.label}</div> <div style={{ fontSize: 10, color: FARVE.textMuted }}>{a.beskrivelse}</div> </div> </label>
 );
 })}
 </div> </div>
 {fejl && <div style={{ color: '#e74c3c', marginBottom: 10, fontSize: 13 }}>{fejl}</div>}
 <div style={{ display: 'flex', gap: 10 }}> <button onClick={submit} disabled={sender || !titel || !beskrivelse || valgteAgenter.length === 0}
 style={{ background: FARVE.primary, color: '#fff', border: 'none', padding: '10px 20px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, opacity: (sender || !titel || !beskrivelse || valgteAgenter.length === 0) ? 0.5 : 1 }}>
 {sender ? 'Starter…' : ` Start session (${valgteAgenter.length} agenter)`}
 </button> <button onClick={onCancel} style={{ background: 'transparent', border: '1px solid #ddd', padding: '10px 20px', borderRadius: 8, cursor: 'pointer' }}>Annullér</button> </div> </div>
 );
}
export default function OrchestratorDashboard() {
 const [sessioner, setSessioner] = useState([]);
 const [oversigt, setOversigt] = useState(null);
 const [valgtId, setValgtId] = useState(null);
 const [visForm, setVisForm] = useState(false);
 const [fejl, setFejl] = useState(null);
 const hent = useCallback(async () => {
 try {
 const [s, o] = await Promise.all([
 api('/os/orchestrator/sessions?limit=50'),
 api('/os/orchestrator/oversigt'),
 ]);
 setSessioner(s.sessioner || []);
 setOversigt(o);
 } catch (e) {
 setFejl(e.message);
 }
 }, []);
 useEffect(() => {
 hent();
 const t = setInterval(hent, 5000);
 return () => clearInterval(t);
 }, [hent]);
 if (valgtId) {
 return <SessionDetail id={valgtId} onTilbage={() => setValgtId(null)} onAction={hent} />;
 }
 const grupperetEfterStatus = sessioner.reduce((acc, s) => {
 const k = s.status === 'AFVENTER_GODKENDELSE' ? 'AFVENTER_GODKENDELSE'
 : s.status === 'IGANG' || s.status === 'IMPLEMENTERING' ? 'AKTIV'
 : 'AFSLUTTET';
 if (!acc[k]) acc[k] = [];
 acc[k].push(s);
 return acc;
 }, {});
 return (
 <div style={{ padding: 20 }}> <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}> <h1 style={{ color: '#fff', margin: 0, fontSize: 24, fontWeight: 700 }}> Orchestrator</h1> <button onClick={() => setVisForm(!visForm)} style={{
 background: FARVE.primary, color: '#fff', border: 'none', padding: '10px 18px',
 borderRadius: 8, cursor: 'pointer', fontWeight: 600,
 }}>+ Ny session</button> </div>
 {fejl && <div style={{ background: '#e74c3c22', color: '#e74c3c', padding: 12, borderRadius: 8, marginBottom: 16 }}>Fejl: {fejl}</div>}
 {oversigt && (
 <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
 {(oversigt.status_fordeling || []).map(s => (
 <div key={s.status} style={{
 background: FARVE.bg, backdropFilter: FARVE.blur, padding: 14, borderRadius: 12,
 borderLeft: `4px solid ${STATUS_FARVER[s.status] || '#7f8c8d'}`,
 }}> <div style={{ fontSize: 11, color: FARVE.textMuted }}>{s.status}</div> <div style={{ fontSize: 24, fontWeight: 700, color: FARVE.text }}>{s.antal}</div> </div>
 ))}
 </div>
 )}
 {visForm && <NySessionForm onCreated={() => { setVisForm(false); hent(); }} onCancel={() => setVisForm(false)} />}
 {grupperetEfterStatus.AFVENTER_GODKENDELSE && (
 <> <h2 style={{ color: FARVE.gold, fontSize: 16, marginTop: 16, marginBottom: 10 }}> Afventer din godkendelse ({grupperetEfterStatus.AFVENTER_GODKENDELSE.length})</h2>
 {grupperetEfterStatus.AFVENTER_GODKENDELSE.map(s => <SessionRow key={s.id} s={s} onClick={() => setValgtId(s.id)} />)}
 </>
 )}
 {grupperetEfterStatus.AKTIV && (
 <> <h2 style={{ color: FARVE.accent, fontSize: 16, marginTop: 24, marginBottom: 10 }}> Aktive ({grupperetEfterStatus.AKTIV.length})</h2>
 {grupperetEfterStatus.AKTIV.map(s => <SessionRow key={s.id} s={s} onClick={() => setValgtId(s.id)} />)}
 </>
 )}
 {grupperetEfterStatus.AFSLUTTET && (
 <> <h2 style={{ color: '#7f8c8d', fontSize: 16, marginTop: 24, marginBottom: 10 }}> Afsluttede ({grupperetEfterStatus.AFSLUTTET.length})</h2>
 {grupperetEfterStatus.AFSLUTTET.map(s => <SessionRow key={s.id} s={s} onClick={() => setValgtId(s.id)} />)}
 </>
 )}
 {sessioner.length === 0 && (
 <div style={{ background: FARVE.bg, backdropFilter: FARVE.blur, padding: 40, borderRadius: FARVE.radius, textAlign: 'center', color: FARVE.textMuted }}> <p>Ingen sessioner endnu.</p> <p style={{ fontSize: 13 }}>Klik på <strong>+ Ny session</strong> for at starte en multi-agent diskussion.</p> </div>
 )}
 </div>
 );
}